<template>
  <div :id="chartId" class="histogram-chart"></div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as d3 from 'd3'

import Utils from '@/utils'

const props = {
  simulations: {
    type: Array,
    required: true
  },
  chamber: {
    type: String,
    required: true
  },
  majority: {
    type: Number,
    required: true
  },
  totalSeats: {
    type: Number,
    required: true
  }
}

const computed = {
  ...mapGetters({
    mobilePhone: 'ux/mobilePhone',
    screenWidth: 'ux/screenWidth',
  }),
  currentDate () {
    return this.$store.getters['aggregateModels/currentDate'](this.chamber)
  },
  chartId () {
    return `${this.chamber}-histogram-chart`
  }
}

const methods = {
  drawChart () {
    const self = this

    this.height = 240

    const chart = d3.select(`#${this.chartId}`)
    this.width = chart._groups[0][0].clientWidth

    chart.selectAll('*').remove()

    this.svg = chart.append('svg')
      .attr('width', this.width)
      .attr('height', this.height)

    this.sims = this.simulations.filter(item => item.value > 100)
    // this.sims = this.simulations

    const x = d3.scaleBand()
      .domain(this.sims.map(d => d.seats))
      .range([this.margin.left, this.width - this.margin.right]).padding(0.1)
    const y = d3.scaleLinear()
      .domain([0, d3.max(this.sims, d => d.value)])
      .range([this.height - this.margin.bottom, this.margin.top])

    let tickValues = this.sims.map(item => item.seats).sort((a, b) => a - b)

    // const xAxis = g => g
    //   .attr('class', 'x-axis')
    //   .attr('transform', `translate(0, ${this.height - this.margin.bottom})`)
    //   .call(d3.axisBottom(x)
    //     .tickSizeOuter(0)
    //     .tickValues(tickValues)
    //   )

    let spacing
    if (this.chamber === 'house') {
      spacing = 1
    } else if (this.chamber === 'president') {
      spacing = 2
    } else if (this.chamber.includes('senate')) {
      spacing = this.mobilePhone ? 3 : 6
    }

    const rectWidth = (self.width / self.sims.length) - spacing
    const divisible = self.chamber.includes('senate') ? 4 : 20

    this.svg
      .on('mouseover', function () {
        d3.selectAll('.seat-rect').style('opacity', 0.5)
      })
      .on('mouseout', function () {
        self.select = null
        d3.selectAll('.seat-rect').style('opacity', 1)
        d3.selectAll('.xAxis-label').classed('hide-label', false)
        d3.selectAll('.gop-seats').style('opacity', (d, i) => {
          if (self.chamber === 'president') {
            return 0
          }
          return i % divisible === 0 ? 1 : 0
        })
        d3.selectAll('.dem-seats').style('opacity', (d, i) => {
          if (self.chamber === 'president') {
            return 0
          }
          return i % divisible === 0 ? 1 : 0
        })
        Utils.debounce(self.$emit('on-mousemove', null), 500)
      })
      // .append('g')
      // .call(xAxis)

    this.svg.append('g')
      .selectAll('rect')
      .data(this.sims)
      .enter().append('rect')
      .attr('class', (d) => `seat-rect rect-${d.seats}`)
      .attr('fill', (d) => {
        if (this.chamber.includes('senate') && d.seats === 50) {
          return '#bdbdbd'
        }
        return d.seats >= this.majority ? 'var(--gop)' : 'var(--dem)'
      })
      .attr('x', d => x(d.seats))
      .attr('width', rectWidth)
      .attr('transform', `translate(0, -${this.margin.top})`)
      .attr('y', d => y(d.value) + this.margin.top)
      .attr('height', d => this.height - this.margin.bottom - y(d.value))

    // draw x-axis
    const xAxis = this.svg.append('g')

    this.svg.append('line')
      .style('stroke-width', 1)
      .style('stroke', this.$vuetify.theme.currentTheme.text)
      .style('fill', 'none')
      .attr('x1', 0 + self.margin.left)
      .attr('y1', self.height - self.margin.bottom)
      .attr('x2', self.width - self.margin.right)
      .attr('y2', self.height - self.margin.bottom)


    xAxis.selectAll('.gop-seats')
      .data(tickValues)
      .enter().append('text')
      .text(function (d) { return `${d}R`})
      .attr('class', (d) => `xAxis-label gop-seats rect-label-${d}`)
      .attr('text-anchor', 'middle')
      .attr('x', d => x(d) + (rectWidth / 2))
      .attr('y', self.height - 10)
      .style('font-size', '12px')
      .style('font-weight', '500')
      .style('fill', 'var(--gop)')
      .style('opacity', (d, i) => {
        if (this.chamber === 'president') {
          return 0
        }
        return i % divisible === 0 ? 1 : 0
      })

    xAxis.selectAll('.dem-seats')
      .data(tickValues)
      .enter().append('text')
      .text(function (d) { return `${self.totalSeats - d}D`})
      .attr('class', (d) => `xAxis-label dem-seats rect-label-${d}`)
      .attr('text-anchor', 'middle')
      .attr('x', d => x(d) + (rectWidth / 2))
      .attr('y', self.height - 25)
      .style('font-size', '12px')
      .style('font-weight', '500')
      .style('fill', 'var(--dem)')
      .style('opacity', (d, i) => {
        if (this.chamber === 'president') {
          return 0
        }
        return i % divisible === 0 ? 1 : 0
      })


    const overlayRects = this.svg.append('g')
      .selectAll('rect')
      .data(this.sims)
      .enter().append('rect')
      .attr('fill', 'rgb(189, 189, 189)')
      .style('opacity', 0)
      .attr('x', d => x(d.seats))
      .attr('width', () => (self.width / self.sims.length))
      .attr('transform', `translate(-3, -${this.margin.top})`)

    overlayRects
      .on('mousemove', function (d) {
        self.selected = d.seats
        var selected = `rect-${d.seats}`;
        d3.selectAll('.seat-rect').style('opacity', function () {
          return (this.classList[1] === selected) ? 1.0 : 0.5;
        })
        d3.selectAll('.xAxis-label').classed('hide-label', (d) => {
         if (d === self.selected) {
           d3.selectAll(`.rect-label-${d}`).style('opacity', 1)
           return false
         }
         return true
        })
        self.$emit('on-mousemove', d)
      })
      .attr('y', 0)
      .attr('height', '100%')

    // const maxValue = d3.max(this.sims, d => d.value)
    // const maxSeats = this.sims.find(item => item.value === maxValue).seats
    const majority = self.sims.find(s => s.seats === self.majority)
    if (majority) {
      this.svg.append('line')
        .style('stroke-width', self.mobilePhone ? 1 : 2)
        .style('stroke', this.$vuetify.theme.currentTheme.text)
        .style('fill', 'none')
        .attr('x1', x(this.majority))
        .attr('y1', () => {
          return this.margin.top
        })
        .attr('x2', x(this.majority))
        .attr('y2', this.height - this.margin.bottom)
        .style('pointer-events', 'none')
    }
      // .attr('y2', () => {
      //   let d = this.sims.find(s => s.seats === self.majority)
      //   return this.height - this.margin.bottom - y(d.value)
      // })

    // const majorityText = this.svg.append('text')
    //   .attr('fill', this.$vuetify.theme.currentTheme.text)
    //   .attr('font-size', '0.75em')
    //   .attr('font-weight', 500)
    //   .attr('text-anchor', () => {
    //     if (this.majority < maxSeats) {
    //       return 'end'
    //     } else {
    //       return 'start'
    //     }
    //   })
    //   .style('opacity', 0.5)
    //   // .text(() => `MAJORITY (${this.majority})`)
    //   .text('majority')
    //   .attr('y', 10)
    //   .attr('x', () => {
    //     if (this.majority < maxSeats) {
    //       return x(this.majority) - 7
    //     } else {
    //       return x(this.majority) + 7
    //     }
    //   })
  }
}

export default {
  props,
  computed,
  methods,
  data () {
    return {
      width: null,
      height: null,
      margin: { top: 10, right: 10, bottom: 40, left: 15 },
      svg: null,
      selected: null
    }
  },
  mounted () {
    // if (document.documentElement.clientWidth < 960) {
    //   this.margin = { top: 0, right: 10, bottom: 0, left: 10 }
    // }
    this.drawChart()
  },
  watch: {
    screenWidth () {
      this.margin = { top: 10, right: 10, bottom: 40, left: 15 }
      if (typeof this.simulations[0] === 'object') {
        this.drawChart()
      }
    },
    currentDate () {
      if (typeof this.simulations[0] === 'object') {
        this.drawChart()
      }
    },
    '$vuetify.theme.isDark'() {
      this.drawChart()
    }
  }
}
</script>

<style>
.hide-label {
  display: none;
}
</style>
